import { Slider as BaseSlider } from '@base-ui/react/slider'
import React from 'react'
import styled from 'styled-components'
import { accents, brand, gray } from 'sutra/tokens/colors'
import { motion } from 'sutra/tokens/motion'

export interface SliderMark {
  value: number
  label: string
}

export interface SliderProps<V extends number | readonly number[]> {
  // A number is a single-thumb slider; an array is a range slider with one
  // thumb per entry. Controlled only.
  value: V
  onChange: (value: V) => void
  // Fires once a change is final (pointer released or a key pressed), not on
  // every step of a drag. It does not fire when nothing changed.
  onChangeCommitted?: (value: V) => void
  min?: number
  max?: number
  step?: number
  disabled?: boolean
  // Accessible name per thumb, in value order. Base UI puts it on the thumb's
  // `input[type=range]`.
  thumbLabels?: readonly string[]
  // Base UI draws no marks: a tick and label sit at each value's percentage
  // of the track.
  marks?: readonly SliderMark[]
}

const Control = styled(BaseSlider.Control)`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  padding-block: 8px;
  touch-action: none;
  user-select: none;
  cursor: pointer;

  &[data-disabled] {
    cursor: not-allowed;
  }
`

const Track = styled(BaseSlider.Track)`
  position: relative;
  width: 100%;
  height: 4px;
  border-radius: 9999px;
  background: ${gray[200]};
  user-select: none;
`

const Indicator = styled(BaseSlider.Indicator)`
  border-radius: 9999px;
  background: ${accents.reflectionBlue[500]};
  user-select: none;

  &[data-disabled] {
    opacity: 0.5;
  }
`

const Thumb = styled(BaseSlider.Thumb)`
  box-sizing: border-box;
  width: 16px;
  height: 16px;
  border: 2px solid ${accents.reflectionBlue[500]};
  border-radius: 50%;
  background: ${brand.white};
  user-select: none;
  transition: box-shadow ${motion.duration.fast}ms ${motion.ease};

  &:hover,
  &[data-dragging] {
    box-shadow: 0 0 0 4px ${accents.reflectionBlue[100]};
  }

  &:has(:focus-visible) {
    outline: 2px solid ${accents.reflectionBlue[500]};
    outline-offset: 2px;
  }

  &[data-disabled] {
    border-color: ${gray[300]};
    box-shadow: none;
    cursor: not-allowed;
  }
`

const Tick = styled.span<{ $percent: number }>`
  position: absolute;
  top: 50%;
  left: ${({ $percent }) => $percent}%;
  width: 2px;
  height: 8px;
  border-radius: 1px;
  background: ${gray[300]};
  transform: translate(-50%, -50%);
  pointer-events: none;
`

const Labels = styled.div`
  position: relative;
  height: 16px;
  user-select: none;
`

const MarkLabel = styled.span<{ $active: boolean; $percent: number }>`
  position: absolute;
  left: ${({ $percent }) => $percent}%;
  font-size: 11px;
  line-height: 16px;
  white-space: nowrap;
  transform: translateX(-50%);
  color: ${({ $active }) =>
    $active ? accents.reflectionBlue[500] : gray[400]};
  font-weight: ${({ $active }) => ($active ? 500 : 400)};
`

const asArray = (value: number | readonly number[]): readonly number[] =>
  typeof value === 'number' ? [value] : value

// A mark is "reached" once the track's filled span covers it: at or below a
// single thumb, or between the outer thumbs of a range.
const isReached = (
  mark: number,
  values: readonly number[],
  min: number,
): boolean => {
  const [first = min] = values
  const last = values[values.length - 1] ?? first
  return values.length > 1
    ? mark >= first && mark <= last
    : mark >= min && mark <= last
}

export const Slider = <V extends number | readonly number[]>({
  value,
  onChange,
  onChangeCommitted,
  min = 0,
  max = 100,
  step = 1,
  disabled,
  thumbLabels,
  marks,
}: SliderProps<V>): React.ReactElement => {
  const values = asArray(value)
  // Base UI rejects `max <= min` (it logs an error), but a caller whose data
  // collapses to one point still has to render. Nothing can move on a track
  // with no span, so show it inert and give Base UI a valid one.
  const collapsed = max <= min
  const trackMax = collapsed ? min + step : max
  const percentOf = (mark: number): number =>
    collapsed ? 0 : ((mark - min) / (max - min)) * 100

  return (
    <BaseSlider.Root
      value={value}
      min={min}
      max={trackMax}
      step={step}
      disabled={disabled || collapsed}
      // Base UI types the callback value as `number` for a number root and as
      // the array otherwise; `V` is that same choice made by the caller.
      onValueChange={(next) => onChange(next as V)}
      onValueCommitted={
        onChangeCommitted && ((next) => onChangeCommitted(next as V))
      }
    >
      <Control>
        <Track>
          <Indicator />
          {marks?.map((mark) => (
            <Tick
              key={mark.value}
              aria-hidden
              $percent={percentOf(mark.value)}
            />
          ))}
          {values.map((_, index) => (
            <Thumb
              key={index}
              index={values.length > 1 ? index : undefined}
              aria-label={thumbLabels?.[index]}
            />
          ))}
        </Track>
      </Control>
      {marks && (
        <Labels aria-hidden>
          {marks.map((mark) => (
            <MarkLabel
              key={mark.value}
              $active={isReached(mark.value, values, min)}
              $percent={percentOf(mark.value)}
            >
              {mark.label}
            </MarkLabel>
          ))}
        </Labels>
      )}
    </BaseSlider.Root>
  )
}

Slider.displayName = 'Slider'
